import { Link } from '@tanstack/react-router';
import { Icon } from '../../components/Icon.tsx';
import { useAuthStore } from '../../store/auth.ts';
import { useZoneStats } from '../../features/zone/api.ts';
import { STATUS_LABEL, type IssueStatus } from '../../features/zone/types.ts';

const STATUSES = Object.keys(STATUS_LABEL) as IssueStatus[];

const TONE: Record<IssueStatus, string> = {
  SUBMITTED: 'text-primary',
  ASSIGNED: 'text-[hsl(var(--tertiary))]',
  IN_PROGRESS: 'text-[hsl(var(--warning))]',
  RESOLVED: 'text-[hsl(var(--success))]',
  CLOSED: 'text-muted-foreground',
};

function Figure({
  label, value, tone, search,
}: { label: string; value: number; tone: string; search: Record<string, string> }): JSX.Element {
  return (
    <Link
      to="/zone"
      search={search as never}
      className="block rounded-2xl px-1 py-2 transition-colors hover:bg-[hsl(var(--surface-high))]"
    >
      <div className="num tabular text-4xl leading-none tracking-tight text-ink md:text-5xl">{value}</div>
      <div className={`mt-2 text-sm font-medium ${tone}`}>{label}</div>
    </Link>
  );
}

export function ZoneDashboard(): JSX.Element {
  const isOrgAdmin = useAuthStore((s) => s.user?.isOrgAdmin ?? false);
  const { data, isLoading, isError } = useZoneStats();

  return (
    <div className="page space-y-6 md:space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <h1 className="text-3xl leading-tight tracking-tight md:text-[2.5rem]">Dashboard</h1>
        {isOrgAdmin && (
          <Link to="/zone/settings" className="btn-ghost">
            <Icon name="tune" size={18} /> Zone settings
          </Link>
        )}
      </header>

      {isLoading ? (
        <p className="status-label" role="status" aria-live="polite">Loading…</p>
      ) : isError || !data ? (
        <p className="text-sm text-destructive" role="alert">Could not load the dashboard. Please try again.</p>
      ) : (
        <>
          <section aria-label="Issues by status" className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 lg:grid-cols-6">
            {STATUSES.map((s) => (
              <Figure key={s} label={STATUS_LABEL[s]} value={data.byStatus[s]} tone={TONE[s]} search={{ status: s }} />
            ))}
            <Figure
              label="Open urgent"
              value={data.openByPriority.URGENT}
              tone="text-destructive"
              search={{ priority: 'URGENT' }}
            />
          </section>

          <section aria-label="Issues by zone" className="space-y-3">
            <h2 className="text-lg font-medium">By zone</h2>
            {data.byZone.length === 0 ? (
              <p className="text-sm text-muted-foreground">No zones yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-3xl border border-[hsl(var(--border)/0.7)]">
                <table className="w-full min-w-[34rem] text-sm">
                  <thead>
                    <tr className="border-b border-[hsl(var(--border)/0.6)] text-start">
                      <th scope="col" className="px-4 py-3 text-start font-medium text-muted-foreground">Zone</th>
                      {STATUSES.map((s) => (
                        <th key={s} scope="col" className={`px-3 py-3 text-end font-medium whitespace-nowrap ${TONE[s]}`}>
                          {STATUS_LABEL[s]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.byZone.map((z) => (
                      <tr key={z.zoneId} className="border-b border-[hsl(var(--border)/0.6)] last:border-b-0">
                        <th scope="row" className="px-4 py-3 text-start font-medium text-ink whitespace-nowrap">
                          <Link to="/zone" search={{ zone: z.zoneId } as never} className="hover:underline">{z.name}</Link>
                        </th>
                        {STATUSES.map((s) => (
                          <td key={s} className="num tabular px-3 py-3 text-end">{z[s]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
